import { RefObject } from 'react';

import { TFunction, useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { snakeCase } from 'snake-case';

import { useAddMediaPlanRowMutation } from '../../api/mediaPlansApiSlice.js';
import {
  getMapFormatValueToName,
  getMapTypeValueToName,
} from '../../constants/campaignBuilder.js';
import { UpdateRowRequest } from '../../types/_campaigns/campaignBuilder.js';
import { BuilderSchemaResponse } from '../../types/builderSchema.js';
import { BuilderTableViewColumn } from '../../types/builderTableView.js';

type Options = {
  wrapperRef: RefObject<HTMLDivElement | null | undefined>;
  columnHeadRef: RefObject<HTMLTableRowElement | undefined>;
  toLeft?: boolean;
  isMediaPanelOpened?: boolean;
};
const scrollOffset = 2;
const SCROLL_COLUMN_STEP = 2;

const NEW_ROW = 'Row Name';

const getColumnIndex = (
  nextColumnIndex: number,
  columnLength: number,
  toLeft?: boolean,
) => {
  if (toLeft) {
    const index = nextColumnIndex - SCROLL_COLUMN_STEP;
    return Math.max(0, index);
  }
  const index = nextColumnIndex + SCROLL_COLUMN_STEP;
  return index >= columnLength ? columnLength - 1 : index;
};

export const scrollTableByColumn = ({
  wrapperRef,
  columnHeadRef,
  toLeft,
  isMediaPanelOpened,
}: Options) => {
  const columns = columnHeadRef?.current?.children || [];

  const lastColumnWidth = isMediaPanelOpened
    ? 0
    : columns[columns.length - 1].clientWidth + scrollOffset;

  const nextColumnIndex = getFirstHiddenElementIndex(
    columns,
    wrapperRef,
    lastColumnWidth,
    toLeft,
  );

  const index = getColumnIndex(nextColumnIndex, columns.length, toLeft);

  const left = getLeftScrollPosition(
    columns[index].getBoundingClientRect(),
    lastColumnWidth,
    wrapperRef,
    toLeft,
  );

  wrapperRef?.current?.scrollTo({
    top: wrapperRef?.current?.scrollTop,
    left,
    behavior: 'smooth',
  });
};

const getFirstHiddenElementIndex = (
  columns: HTMLCollection | never[],
  wrapperRef: RefObject<HTMLDivElement | null | undefined>,
  lastColumnWidth: number,
  toLeft?: boolean,
) => {
  const { scrollLeft = 0, clientWidth = 0 } = wrapperRef.current || {};

  let width = 0;
  const elIndex = Object.keys(columns).findIndex((key) => {
    //@ts-ignore
    width += columns[key].clientWidth;

    if (toLeft) {
      return width > scrollLeft;
    }
    return width > clientWidth + scrollLeft - lastColumnWidth;
  });

  return !!elIndex && toLeft ? elIndex - 1 : elIndex;
};

const getLeftScrollPosition = (
  columnRect: DOMRect,
  lastColumnWidth: number,
  wrapperRef: RefObject<HTMLDivElement | null | undefined>,
  toLeft?: boolean,
) => {
  const { scrollLeft = 0, clientWidth = 0 } = wrapperRef.current || {};

  const { left: blockLeft = 0 } =
    wrapperRef?.current?.getBoundingClientRect() || {};

  let left = scrollLeft - blockLeft;
  if (toLeft) {
    left += columnRect.left - scrollOffset;

    return scrollLeft - left === scrollOffset ? 0 : left;
  }
  left += columnRect.right + lastColumnWidth - clientWidth;
  return left;
};

type ChangeColumnsOrderParams = {
  dragHeaderId: string;
  dropHeaderId: string;
  columnOrder: string[];
  columnsFromSchema: BuilderTableViewColumn[];
};

export const changeColumnsOrder = ({
  dragHeaderId,
  dropHeaderId,
  columnOrder,
  columnsFromSchema,
}: ChangeColumnsOrderParams) => {
  const dragHeaderIdx = columnOrder.findIndex((col) => dragHeaderId === col);
  const dropHeaderIdx = columnOrder.findIndex((col) => dropHeaderId === col);

  const dragHeader = columnOrder[dragHeaderIdx];

  const headers = [...columnOrder];
  headers.splice(dragHeaderIdx, 1);
  headers.splice(dropHeaderIdx, 0, dragHeader);

  const columns = headers.reduce((acc: BuilderTableViewColumn[], col) => {
    const existColumn = columnsFromSchema.find(
      (column) => snakeCase(column.name?.toString() || '') === snakeCase(col),
    );
    if (!existColumn) {
      return acc;
    }

    return [...acc, existColumn];
  }, []);

  return { headers, columns };
};

export const prepareDimensionsData = ({
  commonDimensions,
  otherTechCostFields,
  customColumns,
  t,
}: BuilderSchemaResponse & { t: TFunction }) => {
  const mappedCommonDimensions = commonDimensions.map((group) => ({
    isTechCost:
      group.label === 'Tech costs' || group.label === 'Costi tecnologici',
    label: group.label,
    dimensions:
      group.label === 'Tech costs' || group.label === 'Costi tecnologici'
        ? [
            ...group.dimensions.map((dimension) => ({
              name: dimension.label,
              value: dimension.name,
            })),
            ...otherTechCostFields.map((techCost) => ({
              name: techCost.label,
              value: techCost.name,
              autoTechCostPercent: techCost.autoTechCostPercent,
            })),
          ]
        : group.dimensions.map((dimension) => ({
            name:
              dimension.label === 'Budget' ? 'Total Budget' : dimension.label,
            value: dimension.name,
          })),
    isCustom: false,
  }));

  const mapTypeValueToName = getMapTypeValueToName(t);
  const mapFormatValueToName = getMapFormatValueToName(t);

  const customGroup = {
    label: 'Custom',
    dimensions: customColumns.map((dimension) => {
      return {
        name: dimension.label,
        value: String(dimension.id),
        format: {
          name: mapFormatValueToName[dimension.fieldFormat],
          value: dimension.fieldFormat,
        },
        type: {
          name: mapTypeValueToName[dimension.fieldType],
          value: dimension.fieldType,
        },
        selectOptions: dimension.selectValues.map(({ label, ...rest }) => ({
          ...rest,
          name: label,
        })),
        editable: dimension.editable,
      };
    }),
    isCustom: true,
    isTechCost: false,
  };
  return [customGroup, ...mappedCommonDimensions];
};

export const useAddMediaPlanRow = () => {
  const { t } = useTranslation('@myn/planning');

  const [addMediaPlanRow] = useAddMediaPlanRowMutation();

  const onSuccess = (label: string) => {
    const currentLabel = label === NEW_ROW ? 'New row' : label;
    enqueueSnackbar({
      variant: 'success',
      title: t('{{currentLabel}} added successfully.', {
        currentLabel,
      }),
      autoHideDuration: 3000,
      removable: true,
      textLayout: 'inline',
    });
  };

  const onFailed = (label: string) => {
    const currentLabel = label === NEW_ROW ? 'New row' : label;
    enqueueSnackbar({
      variant: 'error',
      title: t('{{currentLabel}} could not be added. Try again, please.', {
        currentLabel,
      }),
      autoHideDuration: 3000,
      removable: true,
      textLayout: 'inline',
    });
  };

  const handleAddChannelRequest = async (
    row: UpdateRowRequest & {
      channel?: { name: string; label: string };
      label?: string;
    },
    setRowState?: (
      id: number[],
      { isNewAdded }: { isNewAdded: boolean },
    ) => void,
  ) => {
    const { type, parent, mediaPlan, channel } = row;

    const rowParams = channel
      ? { ...row, name: channel.name }
      : { type, parent, mediaPlan };

    try {
      const res = await addMediaPlanRow(rowParams).unwrap();

      const { name, id } = res;
      const newRowName = channel ? channel.label : name;
      onSuccess(newRowName ?? '');
      setRowState?.([id], { isNewAdded: true });
    } catch {
      const name = channel ? channel.label : row.label;
      onFailed(name ?? '');
    }
  };

  const handleAddPlatformRequest = async (
    row: UpdateRowRequest & {
      channel: { name: string; label: string };
      currentChannelId: number | null;
      label: string;
    },
    toggleRowExpanded?: (id: number, isExpanded: boolean) => void,
    setRowState?: (
      id: number[],
      { isNewAdded }: { isNewAdded: boolean },
    ) => void,
  ) => {
    const { type, name, mediaPlan, channel, currentChannelId, parent, label } =
      row;

    try {
      const { id } = await addMediaPlanRow({
        type,
        name,
        parent: parent ?? currentChannelId,
        channelName: channel.name,
        mediaPlan,
      }).unwrap();

      if (currentChannelId) {
        toggleRowExpanded?.(currentChannelId, true);
        toggleRowExpanded?.(id, true);
        onSuccess(label);
        setRowState?.([id], { isNewAdded: true });
      }
    } catch {
      onFailed(label);
    }
  };

  return {
    handleAddChannelRequest,
    handleAddPlatformRequest,
    onSuccess,
    onFailed,
  };
};
